import { IModuleConfig, ISlotConfig } from 'redux-state/layout/types.modules.js';
import { IModuleConfigState, ModuleTypeEnum } from 'redux-state/modules/types.js';
import { AnyObjectType } from 'utilities/helpers/object.scaffold.js';
import { getFirstOfArr } from 'utilities/isType.js';
import { SCREEN_SIZES } from 'utilities/media-queries';

export type NullableAnyObject = AnyObjectType | undefined;

export type ILayoutShowHideIfNull = {
  value?: string;
  /** a comma delimited list of values to match against the key - e.g. "value1,value2,value3" or "value1" */
  contains?: string;
  appliesTo?: SCREEN_SIZES[];
};

export type ILayoutHideWhen<T extends NullableAnyObject = {}> = T & {
  hideWhen?: SCREEN_SIZES[];
  /** Hides the module if the value is generated as "null" (used for dynamic values in the layout) */
  hideIfContains?: ILayoutShowHideIfNull;
  /** Hides the module if the value is NOT generated as "null" (used for dynamic values in the layout) */
  hideIfNotContains?: ILayoutShowHideIfNull;
};

/** If dataurl has hide module then dont display on sidebar */
export const hideHiddenModules = ({ hideModule }: IModuleConfig): boolean => hideModule !== true;

/** Should item display.  Hides if any of the hiding criteria apply */
export function shouldDisplayWithHideWhen<T extends NullableAnyObject>(
  config: ILayoutHideWhen<T> | undefined,
  screenSize: SCREEN_SIZES,
): boolean {
  /* Due to back end errors configs can sometimes be misconstructed e.g. {TOWN-BELT}
   * Best place to check that is here and hide if it is to avoid errors */
  const hideWhen = config?.hideWhen ?? [];
  if (hideWhen.includes(screenSize)) {
    return false;
  }

  const {hideIfContains, hideIfNotContains} = config ?? {};

  // Hide if a specified list of items contains the value - and there's either no screen size specified or it applies to the current screen size 
  if (hideIfContains?.value != null && hideIfContains?.contains != null) {
    if (hideIfContains.contains.toLowerCase().split(',').includes(hideIfContains.value.toLowerCase())) {
      if (hideIfContains.appliesTo?.includes(screenSize) !== false) {
        return false;
      }
    }
  }

  // Hide if a specified list of items does NOT contain the value - and there's either no screen size specified or it applies to the current screen size 
  if (hideIfNotContains?.value != null && hideIfNotContains?.contains != null) {
    if (!(hideIfNotContains.contains.toLowerCase().split(',').includes(hideIfNotContains.value.toLowerCase()))) {
      if (hideIfNotContains.appliesTo?.includes(screenSize) !== false) {
        return false;
      }
    }
  }

  return true;
}

export const getSelectedConfig = <T extends NullableAnyObject>(
  configs: Array<ILayoutHideWhen<T>>,
  layout: SCREEN_SIZES,
): ILayoutHideWhen<T> | undefined => {
  const filteredConfigs = configs.filter((config) => {
    return shouldDisplayWithHideWhen(config, layout);
  });
  return getFirstOfArr(filteredConfigs);
};

export function filterOutHiddenModules(currentScreenSize: SCREEN_SIZES, modArr: IModuleConfig[] = []): IModuleConfig[] {
  const modules = modArr.filter(({ layout }) => {
    return shouldDisplayWithHideWhen(layout, currentScreenSize);
  });
  return modules;
}

export const getSlot = (slot?: ISlotConfig): ISlotConfig => (!slot?.modules ? { modules: [] } : slot);

export const buildSlot = (slot?: ISlotConfig): ISlotConfig => (!slot?.modules ? { modules: [] } : slot);

export const scaffoldModuleTypes = (modules: IModuleConfig[] = []): IModuleConfigState[] => {
  const newMods = modules.map((m) => {
    if (m.dataUrl) {
      const dynModule: IModuleConfigState = {
        type: { module: ModuleTypeEnum.ModuleDynamic, dynamicUrl: m.dataUrl },
        data: m,
      };
      return dynModule;
    }
    const staticModule: IModuleConfigState = {
      type: { module: ModuleTypeEnum.ModuleStatic },
      data: m,
    };
    return staticModule;
  });
  return newMods;
};
